import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { updateAgreementRequirementSettings } from '@user/store/closingRequirements/api'
import { ClosingAgreementRequirementsModel } from '@user/store/closingRequirements/typings'
import { useAppDispatch } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import AgreementIcon from '../../atoms/Icon/AgreementIcon'
import { useGetAgreementRequirementsOnMount } from '../../hooks/useGetAgreementRequirementsOnMount'
import { AgreementsRequirement } from './AgreementsRequirement'
import { RequirementsHeader } from '../RequirementsHeader'
import { Theme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import {
  selectCurrentUser,
  setAgreementAutoCloseSettingStatus,
} from '@user/store/authorization'
import { useSelector } from 'react-redux'
import { ClosingRequirementsForm } from '@user/components/Settings/Home/ClosingRequirementsForm'

interface Props {
  onClose: () => void
}

export const AgreementRequirementForm: React.FC<Props> = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()
  const currentUser = useSelector(selectCurrentUser)

  const [requirements, setRequirements] = useState<
    ClosingAgreementRequirementsModel[]
  >([])

  const [autoCloseAgreements, setAutoCloseAgreements] = useState<boolean>(false)

  const { agreementRequirementsStatus, agreementRequirements } =
    useGetAgreementRequirementsOnMount()

  const onSelected = useCallback(
    (model: ClosingAgreementRequirementsModel) => {
      const items = requirements.filter(
        (x) => x.salesFunnelId != model.salesFunnelId,
      )
      setRequirements(items.concat(model))
    },
    [requirements, setRequirements],
  )

  useEffect(() => {
    setRequirements(agreementRequirements)
  }, [agreementRequirements, setRequirements])

  useEffect(() => {
    setAutoCloseAgreements(currentUser.agreementAutoCloseSetting)
  }, [currentUser.agreementAutoCloseSetting])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)
    const result = await dispatch(
      updateAgreementRequirementSettings({ autoCloseAgreements, requirements }),
    )
    if (updateAgreementRequirementSettings.fulfilled.match(result)) {
      setIsLoading(false)
      toast.success('Agreement Closing Requirements have been updated.')
      dispatch(setAgreementAutoCloseSettingStatus(autoCloseAgreements))
    } else {
      setIsLoading(false)
      toast.error(
        'We are unable to update Agreement Closing Requirements at this time.',
      )
    }
    setTimeout(() => {
      onClose()
    }, 0)
  }, [autoCloseAgreements, dispatch, onClose, requirements])

  const handleAutoCloseAgreement = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoCloseAgreements(event.target.checked)
    },
    [],
  )

  return (
    <ClosingRequirementsForm
      onSave={handleSubmit}
      onCancel={onClose}
      secondaryBtnLabel="Cancel"
      padding="0 20px"
      disableSave={isLoading}
      primaryBtnLabel="Save"
    >
      {agreementRequirementsStatus === 'pending' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {agreementRequirementsStatus === 'fail' && (
        <Box
          sx={{
            paddingTop: (theme: Theme) => theme.spacing(4.8),
            textAlign: 'center',
          }}
        >
          <Alert severity="info">Failed to load Closing Requirements.</Alert>
        </Box>
      )}

      {agreementRequirementsStatus === 'success' && (
        <>
          <RequirementsHeader
            title="Closing Requirements for Agreements"
            subTitle="Prevent sales from being closed/won without an agreement.  Optionally enforce a customer signature."
            icon={<AgreementIcon></AgreementIcon>}
          />

          <Typography variant="h6" mt={2}>
            Agreement Settings
          </Typography>
          <FormControlLabel
            sx={{ mt: 2 }}
            control={
              <Switch
                checked={autoCloseAgreements}
                onChange={handleAutoCloseAgreement}
                name="autoCloseAgreement"
              />
            }
            label="Automatically cancel agreements when a lead is disqualified or an opportunity is closed/lost"
          />
          <Box></Box>

          <Typography variant="h6" mt={3}>
            Sales Funnel Agreement Settings
          </Typography>
        </>
      )}

      {agreementRequirements &&
        agreementRequirements.map((x) => (
          <AgreementsRequirement
            onSelected={onSelected}
            key={x.salesFunnelId}
            {...x}
          ></AgreementsRequirement>
        ))}
    </ClosingRequirementsForm>
  )
}
